import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type ParameterSpec, useCompetitions, useFixtures } from "../../api/hooks";
import { formatInstant } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Field, Input, Select } from "../../components/ui/fields";

type Value = string | number | boolean;
const DATE_RULES = new Set(["today", "yesterday", "tomorrow"]);

/** Today's calendar date in a zone, `yyyy-mm-dd`. */
function today(timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

function initial(spec: ParameterSpec): Value {
  if (spec.type === "boolean") return spec.default === true;
  if (spec.type === "date" && typeof spec.default === "string" && DATE_RULES.has(spec.default)) return "";
  return typeof spec.default === "string" || typeof spec.default === "number" ? spec.default : "";
}

function schemaFor(specs: readonly ParameterSpec[], requiredMessage: string) {
  const shape: Record<string, z.ZodType> = {};
  for (const spec of specs) {
    if (spec.type === "boolean") {
      shape[spec.name] = z.boolean();
      continue;
    }
    if (spec.type === "integer" || spec.type === "number") {
      let number = z.coerce.number();
      if (spec.type === "integer") number = number.int();
      if (spec.minimum !== null) number = number.min(spec.minimum);
      if (spec.maximum !== null) number = number.max(spec.maximum);
      // An empty box is missing, not zero, which is what coercion would make it.
      shape[spec.name] = spec.required ? z.unknown().refine((value) => value !== "", requiredMessage).pipe(number) : z.union([z.literal(""), number]);
      continue;
    }
    shape[spec.name] = spec.required ? z.string().trim().min(1, requiredMessage) : z.string();
  }
  return z.object(shape);
}

/** Choose a game: competition, then date, then that date's fixtures. */
function FixturePicker({ id, value, onChange }: { id: string; value: string; onChange(value: string): void }) {
  const { t } = useTranslation();
  const competitions = useCompetitions(true);
  const [competitionId, setCompetitionId] = useState("");
  const competition = competitions.data?.find((item) => item.id === competitionId) ?? competitions.data?.[0];
  const timeZone = competition?.timeZone ?? "UTC";
  // Until the user picks a date, today in the competition's zone, once that is known.
  const [picked, setPicked] = useState("");
  const date = picked || today(timeZone);
  const fixtures = useFixtures(competition?.id ?? "", date, timeZone);
  // A game chosen for another competition or date must be chosen again.
  const choose = (change: () => void) => {
    change();
    onChange("");
  };
  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
      <Select aria-label={t("run.pickCompetition")} value={competition?.id ?? ""} onChange={(event) => choose(() => setCompetitionId(event.target.value))}>
        {competitions.data?.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </Select>
      <Input aria-label={t("run.pickDate")} type="date" value={date} onChange={(event) => choose(() => setPicked(event.target.value))} />
      <Select id={id} className="sm:col-span-2" value={value} onChange={(event) => onChange(event.target.value)} disabled={!fixtures.data?.length}>
        <option value="">{fixtures.isPending ? t("states.loading") : fixtures.isError ? t("run.gamesFailed") : fixtures.data?.length ? t("run.chooseGame") : t("run.noGames")}</option>
        {fixtures.data?.map((fixture) => (
          <option key={fixture.id} value={fixture.id}>
            {fixture.label}
            {fixture.startsAt ? ` · ${formatInstant(fixture.startsAt)}` : ""}
          </option>
        ))}
      </Select>
    </div>
  );
}

export function ParameterForm({
  parameters,
  pending,
  onRun,
}: {
  parameters: readonly ParameterSpec[];
  pending: boolean;
  onRun(params: Record<string, Value>): void;
}) {
  const { t } = useTranslation();
  const schema = schemaFor(parameters, t("run.required"));
  const form = useForm<Record<string, Value>>({
    resolver: zodResolver(schema) as never,
    defaultValues: Object.fromEntries(parameters.map((spec) => [spec.name, initial(spec)])),
  });

  // Leave empty optional values out, so the server applies their defaults and date rules.
  const submit = form.handleSubmit((values) => {
    onRun(Object.fromEntries(Object.entries(values).filter(([, value]) => value !== "")));
  });

  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
      {parameters.map((spec) => {
        const id = `param-${spec.name}`;
        const error = form.formState.errors[spec.name]?.message as string | undefined;
        const hint = spec.description ?? (spec.type === "date" && typeof spec.default === "string" && DATE_RULES.has(spec.default) ? `${spec.default} (${spec.timeZone})` : undefined);
        return (
          <Field key={spec.name} label={spec.label} htmlFor={id} hint={hint} error={error}>
            {spec.type === "fixture" ? (
              <Controller control={form.control} name={spec.name} render={({ field }) => <FixturePicker id={id} value={String(field.value ?? "")} onChange={field.onChange} />} />
            ) : spec.type === "enum" ? (
              <Select id={id} {...form.register(spec.name)}>
                {spec.required ? <option value="">{t("run.choose")}</option> : null}
                {spec.values?.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            ) : spec.type === "boolean" ? (
              <input id={id} type="checkbox" className="h-4 w-4" {...form.register(spec.name)} />
            ) : spec.type === "integer" || spec.type === "number" ? (
              <Input id={id} type="number" step={spec.type === "integer" ? 1 : "any"} min={spec.minimum ?? undefined} max={spec.maximum ?? undefined} {...form.register(spec.name)} />
            ) : spec.type === "date" ? (
              <Input id={id} type="date" {...form.register(spec.name)} />
            ) : (
              <Input id={id} {...form.register(spec.name)} />
            )}
          </Field>
        );
      })}
      <Button type="submit" disabled={pending}>
        {pending ? t("run.submitting") : t("run.submit")}
      </Button>
    </form>
  );
}
